import { familyService, type FamilyGoal } from "@/services/familyService";
import { EncryptionService } from "@/services/encryptionService";
import {
  buildPrimaryMemberPayload,
  parseAddFamilyMemberError,
} from "@/hooks/useOptimizedFamilyHelpers";
interface FamilyMemberLike {
  id: string;
  pan_number?: string | null;
  encrypted_pan_number?: string | null;
}

export interface FamilyDataLike {
  family?: { id: string } | null;
  familyMembers?: FamilyMemberLike[];
}

export interface PrimaryMemberInput {
  full_name: string;
  date_of_birth?: string;
  pan_number?: string;
}

export interface FamilyGoalInput {
  goal_name: string;
  target_amount: number;
  current_amount?: number;
  target_date?: string;
  category?: string;
  description?: string;
  currency?: string;
  owner_member_id?: string | null;
  goal_type?: string;
  priority?: string | null;
  monthly_contribution_amount?: number | null;
  sip_auto_enabled?: boolean;
  sip_day_of_month?: number | null;
}

export interface FamilyMemberInput {
  full_name: string;
  email?: string;
  relationship: string;
  pan_number: string;
  role: "ADMIN" | "SPOUSE" | "CHILD" | "DEPENDENT";
  date_of_birth?: string;
}

export interface FamilyMemberUpdate {
  full_name?: string;
  email?: string;
  relationship?: string;
  role?: "ADMIN" | "SPOUSE" | "CHILD" | "DEPENDENT";
  date_of_birth?: string;
  pan_number?: string;
  encrypted_pan_number?: string;
  pan_encryption_iv?: string;
  masked_pan?: string;
}

export function toastErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * Create Family Group With Pan.
 */
export async function createFamilyGroupWithPan(
  userId: string,
  familyName: string,
  primaryMember: PrimaryMemberInput
): Promise<{
  family: Awaited<ReturnType<typeof familyService.createFamilyGroup>>;
  panEncryptionSkipped: boolean;
}> {
  const encryptionService = EncryptionService.getInstance();
  const { payload, panEncryptionSkipped } = await buildPrimaryMemberPayload(
    userId,
    primaryMember,
    (uid, pan) => encryptionService.encryptPANData(uid, pan)
  );

  const family = await familyService.createFamilyGroup({
    familyName,
    primaryMember: payload,
  });

  return { family, panEncryptionSkipped };
}

/**
 * Create Family Goal For Family.
 */
export function createFamilyGoalForFamily(
  familyId: string,
  goalData: FamilyGoalInput
): Promise<FamilyGoal> {
  return familyService.createFamilyGoal(familyId, {
    goal_name: goalData.goal_name,
    target_amount: goalData.target_amount,
    current_amount: goalData.current_amount || 0,
    target_date: goalData.target_date,
    category: goalData.category,
    description: goalData.description,
    currency: goalData.currency || "INR",
    owner_member_id: goalData.owner_member_id ?? undefined,
    priority: goalData.priority ?? undefined,
    monthly_contribution_amount: goalData.monthly_contribution_amount ?? undefined,
    sip_auto_enabled: goalData.sip_auto_enabled,
    sip_day_of_month: goalData.sip_day_of_month ?? undefined,
  });
}

/**
 * Add Family Member With Encrypted Pan.
 */
export async function addFamilyMemberWithEncryptedPan(
  familyId: string,
  userId: string,
  memberData: FamilyMemberInput
) {
  const encryptionService = EncryptionService.getInstance();
  const encryptedPANData = await encryptionService.encryptPANData(userId, memberData.pan_number);

  return familyService.addMember(familyId, {
    full_name: memberData.full_name,
    email: memberData.email,
    relationship: memberData.relationship,
    pan_number: memberData.pan_number,
    encrypted_pan_number: encryptedPANData.encrypted_pan_number,
    pan_encryption_iv: encryptedPANData.pan_encryption_iv,
    masked_pan: encryptedPANData.masked_pan,
    role: memberData.role,
    date_of_birth: memberData.date_of_birth,
    is_active: true,
  });
}

export function resolveAddFamilyMemberToastMessage(error: unknown): string {
  return parseAddFamilyMemberError(error);
}

/**
 * Encrypt PAN for a member update when the user is setting a PAN, or when
 * backfilling encryption for an existing plaintext PAN.
 *
 * Fail closed when the user is actively saving a PAN: never send plaintext
 * pan_number without encrypted_pan_number (DB trigger enforce_pan_encryption
 * rejects that with a 500). Soft-fail on opportunistic backfill so DOB-only
 * updates still succeed.
 */
export async function applyPanEncryptionToMemberUpdates(
  userId: string | undefined,
  member: FamilyMemberLike | undefined,
  updates: FamilyMemberUpdate
): Promise<FamilyMemberUpdate> {
  const trimmedIncomingPan = updates.pan_number?.trim() ?? "";
  const settingPan = Boolean(trimmedIncomingPan);
  const existingPan = member?.pan_number?.trim() || "";
  const needsBackfill = !settingPan && Boolean(existingPan) && !member?.encrypted_pan_number;
  const panToEncrypt = (settingPan ? trimmedIncomingPan : existingPan).toUpperCase();

  if (!panToEncrypt || !userId || (!settingPan && !needsBackfill)) {
    return updates;
  }

  try {
    const encryptionService = EncryptionService.getInstance();
    const encryptedPANData = await encryptionService.encryptPANData(userId, panToEncrypt);
    return {
      ...updates,
      pan_number: panToEncrypt,
      encrypted_pan_number: encryptedPANData.encrypted_pan_number,
      pan_encryption_iv: encryptedPANData.pan_encryption_iv,
      masked_pan: encryptedPANData.masked_pan,
    };
  } catch (error) {
    console.error("Failed to encrypt PAN during update:", error);
    if (settingPan) {
      throw error instanceof Error
        ? error
        : new Error("Failed to encrypt PAN data. Please try again.");
    }
    return updates;
  }
}

/**
 * Update Family Member With Optional Pan Encryption.
 */
export async function updateFamilyMemberWithOptionalPanEncryption(
  familyData: FamilyDataLike | undefined,
  userId: string | undefined,
  memberId: string,
  updates: FamilyMemberUpdate
) {
  const member = familyData?.familyMembers?.find((m) => m.id === memberId);
  const nextUpdates = await applyPanEncryptionToMemberUpdates(userId, member, updates);

  const familyId = familyData?.family?.id;
  if (!familyId) {
    throw new Error("Family ID not found. Please refresh the page.");
  }
  if (!memberId) {
    throw new Error("Member ID is required");
  }

  return familyService.updateMember(memberId, nextUpdates, familyId);
}

export function requireFamilyId(familyId: string | undefined): string {
  if (!familyId) {
    throw new Error("Family ID not found. Please refresh the page.");
  }
  return familyId;
}

export function requireUserId(userId: string | undefined): string {
  if (!userId) {
    throw new Error("User not authenticated");
  }
  return userId;
}

export function requireUserAndFamily(
  userId: string | undefined,
  familyId: string | undefined
): { userId: string; familyId: string } {
  if (!userId || !familyId) {
    throw new Error("User not authenticated or no family found");
  }
  return { userId, familyId };
}
